"use client";

import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { useSession } from "@/lib/auth/client";

type WishlistContextValue = {
  savedIds: ReadonlySet<number>;
  toggle: (productId: number) => Promise<void>;
  pendingId: number | null;
};

const WishlistContext = createContext<WishlistContextValue | null>(null);

export function WishlistProvider({ children }: { children: React.ReactNode }) {
  const [savedIds, setSavedIds] = useState<Set<number>>(() => new Set());
  const [pendingId, setPendingId] = useState<number | null>(null);
  const { data: session } = useSession();

  useEffect(() => {
    if (!session?.user) return;
    let active = true;
    fetch("/api/wishlist")
      .then(async (response) => (response.ok ? response.json() : null))
      .then((body: { productIds?: number[] } | null) => {
        if (active && body?.productIds) setSavedIds(new Set(body.productIds));
      })
      .catch(() => undefined);
    return () => {
      active = false;
    };
  }, [session?.user]);

  const toggle = useCallback(async (productId: number) => {
    setPendingId(productId);
    try {
      const response = await fetch("/api/wishlist", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ productId }),
      });
      const body = await response.json();
      if (!response.ok) throw new Error(body.error || "Could not update wishlist.");
      setSavedIds((current) => {
        const next = new Set(current);
        if (body.saved) next.add(productId);
        else next.delete(productId);
        return next;
      });
      toast.success(body.saved ? "Saved to wishlist." : "Removed from wishlist.");
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Could not update wishlist.");
    } finally {
      setPendingId(null);
    }
  }, []);

  const value = useMemo(() => ({ savedIds, toggle, pendingId }), [savedIds, toggle, pendingId]);
  return <WishlistContext.Provider value={value}>{children}</WishlistContext.Provider>;
}

export function useWishlist() {
  const context = useContext(WishlistContext);
  if (!context) throw new Error("useWishlist must be used within WishlistProvider");
  return context;
}
